import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Api from 'entities/helpers/api';
import EverTrue from 'app';

export const useFolderDetails = (id) => {
  const [folderLists, setFolderLists] = useState([]);
  const [total, setTotal] = useState(0);
  const [folderName, setFolderName] = useState('');
  const [folderType, setFolderType] = useState('');
  const [folderOwner, setFolderOwner] = useState();
  const [loading, setLoading] = useState(true);

  // Sorting
  const sortableFields = useMemo(() => ['name', 'updated_at'], []);
  const sortPrefsLoaded = useRef(false);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);

  // Once folderType is known after the first fetch, load saved sort prefs from localStorage
  useEffect(() => {
    if (folderType && !sortPrefsLoaded.current) {
      const key = folderType === 'DYNAMIC' ? 'EvertrueSSFolderTableSettings' : 'EvertrueListsFolderTableSettings';
      try {
        const settings = JSON.parse(localStorage.getItem(key)) || {};
        const savedField = sortableFields.includes(settings.sortField) ? settings.sortField : 'name';
        const savedOrder = settings.sortOrder === 'desc' ? 'desc' : 'asc';
        setSortModel([{ field: savedField, sort: savedOrder }]);
      } catch {
        // ignore parse errors
      }
      sortPrefsLoaded.current = true;
    }
  }, [folderType, sortableFields]);

  // Persist sort preferences whenever sortModel changes (once folderType is known)
  useEffect(() => {
    if (!folderType || !sortModel[0] || !sortableFields.includes(sortModel[0].field)) return;
    const key = folderType === 'DYNAMIC' ? 'EvertrueSSFolderTableSettings' : 'EvertrueListsFolderTableSettings';
    localStorage.setItem(key, JSON.stringify({ sortField: sortModel[0].field, sortOrder: sortModel[0].sort }));
  }, [sortModel, folderType, sortableFields]);

  // Search
  const [search, setSearch] = useState('');

  // Pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  /**
   * Updates the search query and resets pagination to page 0.
   */
  const handleSearchChange = useCallback((query) => {
    setSearch(query);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Updates the sort model and resets pagination to page 0.
   */
  const handleSortChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // API to get folder lists with sorting, pagination, and search params
  const getFolderLists = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      };
      if (sortModel[0] && sortableFields.includes(sortModel[0].field)) {
        params.sort_column = sortModel[0].field;
        params.sort_order = sortModel[0].sort;
      }
      if (search) {
        params.search = search;
      }
      const resp = await Api.CONTACTS.FOLDER_LIST.get({
        urlArgs: { id: id },
        params,
      });
      setFolderListsMapping(resp);
    } catch (error) {
      console.error('Failed to fetch folder lists', error);
      EverTrue.Alert.error('Failed to fetch folder lists');
    } finally {
      setLoading(false);
    }
  }, [id, paginationModel, sortModel, sortableFields, search]);

  // Get folder lists mapping on component mount
  useEffect(() => {
    getFolderLists();
  }, [getFolderLists]);

  const setFolderListsMapping = (folderLists) => {
    setFolderName(folderLists.title);
    setFolderLists(folderLists.contactLists);
    setTotal(folderLists.total);
    setFolderType(folderLists.type);
    setFolderOwner(folderLists.userId);
  };

  // API to update a folder
  const updateFolder = (newName) => {
    return Api.CONTACTS.FOLDER.put({
      urlArgs: { id: id },
      data: JSON.stringify({ title: newName, type: folderType }),
    }).then(() => {
      setFolderName(newName);
      EverTrue.Alert.success('Folder name updated');
    });
  };

  // API to delete a list from folder
  const removeListFromFolder = (folderId, listId) => {
    return Api.CONTACTS.FOLDER_LISTS.delete({
      params: { folder_id: folderId, list_id: listId },
      disableAlerts: true,
    });
  };

  // API to delete the folder
  const deleteFolder = (folderId) => {
    return Api.CONTACTS.FOLDER.delete({
      urlArgs: { id: folderId },
    }).then(() => {
      setTimeout(() => {
        if (EverTrue.UrlManager.hasPreviousPath()) {
          EverTrue.Alert.success(`${folderName} deleted`);
          const actionType = folderType === 'STATIC' ? 'list_action' : 'saved_search_action';
          EverTrue.track.set(actionType, { type: 'delete_folder' });
          setTimeout(() => window.history.back(), 0);
        } else {
          let route = folderType === 'STATIC' ? '/lists' : '/segments';
          const actionType = folderType === 'STATIC' ? 'list_action' : 'saved_search_action';
          EverTrue.track.set(actionType, { type: 'delete_folder' });
          EverTrue.Alert.success(`${folderName} deleted`);
          EverTrue.Navigator(route, { trigger: true });
        }
      }, 300);
    });
  };

  // Update folder lists mapping when lists are removed from the folder or deleted entirely
  const updateFolderLists = (listIds) => {
    const updatedLists = folderLists.filter((list) => !listIds.includes(list.id));
    setFolderLists(updatedLists);
    setTotal((prev) => prev - listIds.length);
  };

  return {
    total,
    folderLists,
    folderName,
    folderType,
    folderOwner,
    loading,
    search,
    handleSearchChange,
    sortModel,
    handleSortChange,
    paginationModel,
    setPaginationModel,
    updateFolder,
    removeListFromFolder,
    deleteFolder,
    updateFolderLists,
  };
};
